// HeaderToolbar.jsx
import React, { useEffect, useState } from 'react';
import { LogIn, Search, Loader2, X } from 'lucide-react';
import fmcLogo from '../assets/fmc_logo.JPG';
import { Link, useLocation } from 'react-router-dom';
import { getEmpanelmentByRefNo } from '../api/providerApi';
import { mapEmpanelmentDataToStore } from '../store/mapEmpanelmentToStore';
import useCityStore from '../store/cityStore';
import useAreaStore from '../store/areaStore';
import useRegistrationStore from '../store/useRegistrationStore';
import firstCareLogo from '../assets/firstcare.svg';

export default function HeaderToolbar() {
    const [hideText, setHideText] = useState(false);
    const [refNumber, setRefNumber] = useState('');
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchError, setSearchError] = useState('');

    const location = useLocation();

    // Store actions we'll need
    const { fetchCities } = useCityStore();
    const { fetchAreas } = useAreaStore();
    const { updateProviderData } = useRegistrationStore();

    const handleLoginRedirect = () => {
        window.location.href = 'https://realtime.fmcnetwork.net/';
    };

    const handleSearchByRefNumber = async () => {
        if (!refNumber.trim()) {
            setSearchError('Please enter a reference number');
            return;
        }

        setSearchLoading(true);
        setSearchError('');

        try {
            const store = useRegistrationStore.getState();
            store.clearRegistration();
            const empanelmentData = await getEmpanelmentByRefNo(refNumber.trim());

            if (empanelmentData) {
                // Map data to global store
                mapEmpanelmentDataToStore(empanelmentData);

                // Optional: set selected type if needed in UI
                if (empanelmentData.ProviderType_ID) {
                    updateProviderData({ providerType: empanelmentData.ProviderType_ID });
                }

                // Load dependent dropdowns
                if (empanelmentData.Provider_Address_State_ID) {
                    await fetchCities(empanelmentData.Provider_Address_State_ID);
                }
                if (empanelmentData.Provider_Address_City_ID) {
                    await fetchAreas(empanelmentData.Provider_Address_City_ID);
                }
                setRefNumber('');
                // Navigate to step-1 if not already on a step page
                if (!location.hash.includes('step')) {
                    window.location.href = '/#step-1';
                }
            } else {
                setSearchError('No empanelment found with this reference number');
            }
        } catch (error) {
            console.error('Error loading empanelment:', error);
            setSearchError(error.message || 'Failed to load empanelment data');
        } finally {
            setSearchLoading(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleSearchByRefNumber();
        }
    };

    useEffect(() => {
        const handleScroll = () => {
            setHideText(window.scrollY > 60);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    return (
        <header className="sticky top-0 z-50 bg-gradient-to-br from-primary/20 to-primary/1 backdrop-blur-sm">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex items-center justify-between h-20 md:h-24 transition-all">

                    {/* Logo + Brand */}
                    <div className="flex items-center gap-3 sm:gap-4">
                        <Link to="/">
                            <img
                                src={fmcLogo}
                                alt="FMC Logo"
                                className="w-32 h-32 object-contain cursor-pointer"
                            />
                        </Link>


                    </div>

                    {/* Login + Search + First Care Logo */}
                    <div className="flex items-center gap-3 sm:gap-4">
                        {/* Beautiful Rounded Search Box + First Care Logo - Desktop */}
                        <div className="hidden md:flex items-center gap-4">
                            <div className="relative">
                                <div className="relative w-80 lg:w-96">
                                    <div className="relative flex items-center">
                                        <input
                                            type="text"
                                            value={refNumber}
                                            onChange={(e) => {
                                                setRefNumber(e.target.value);
                                                setSearchError('');
                                            }}
                                            onKeyPress={handleKeyPress}
                                            placeholder="Search by Reference Number"
                                            className="w-full pl-11 pr-12 py-3 bg-white border-2 border-gray-200 rounded-full 
                                                       shadow-sm hover:shadow-md hover:border-primary/30
                                                       focus:ring-2 focus:ring-primary/20 focus:border-primary 
                                                       outline-none text-sm transition-all duration-200
                                                       disabled:bg-gray-50 disabled:cursor-not-allowed"
                                            disabled={searchLoading}
                                        />
                                        <Search
                                            size={20}
                                            className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                                        />

                                        {/* Search Button */}
                                        <button
                                            type="button"
                                            onClick={handleSearchByRefNumber}
                                            disabled={searchLoading}
                                            className="absolute right-2 top-1/2 -translate-y-1/2 
                                                       bg-primary hover:bg-primary/90 text-white 
                                                       p-2 rounded-full transition-colors
                                                       disabled:bg-gray-300 disabled:cursor-not-allowed
                                                       focus:outline-none focus:ring-2 focus:ring-primary/50"
                                            aria-label="Search"
                                        >
                                            {searchLoading ? (
                                                <Loader2 size={18} className="animate-spin" />
                                            ) : (
                                                <Search size={18} />
                                            )}
                                        </button>
                                    </div>

                                    {/* Error message */}
                                    {searchError && (
                                        <div className="absolute top-full mt-2 left-0 right-0 bg-red-50 border border-red-200 rounded-lg px-3 py-2 flex items-start gap-2 shadow-sm z-10">
                                            <X size={16} className="text-red-500 mt-0.5 flex-shrink-0" />
                                            <span className="text-xs text-red-700">{searchError}</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* First Care Logo */}
                            <Link to="/">
                                <div className="flex items-center">
                                    <img
                                        src={firstCareLogo}
                                        alt="First Care Logo"
                                        className="h-12 lg:h-14 w-auto object-contain"
                                    />
                                </div>
                            </Link>

                        </div>
                    </div>
                </div>

                {/* Beautiful Rounded Search Box - Mobile */}
                <div className="md:hidden pb-4">
                    <div className="relative">
                        <div className="relative flex items-center">
                            <input
                                type="text"
                                value={refNumber}
                                onChange={(e) => {
                                    setRefNumber(e.target.value);
                                    setSearchError('');
                                }}
                                onKeyPress={handleKeyPress}
                                placeholder="Search by Reference Number"
                                className="w-full pl-11 pr-12 py-3 bg-white border-2 border-gray-200 rounded-full 
                                           shadow-sm focus:ring-2 focus:ring-primary/20 focus:border-primary 
                                           outline-none text-sm transition-all
                                           disabled:bg-gray-50"
                                disabled={searchLoading}
                            />
                            <Search
                                size={18}
                                className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                            />

                            {/* Search Button - Mobile */}
                            <button
                                type="button"
                                onClick={handleSearchByRefNumber}
                                disabled={searchLoading}
                                className="absolute right-2 top-1/2 -translate-y-1/2 
                                           bg-primary hover:bg-primary/90 text-white 
                                           p-2 rounded-full transition-colors
                                           disabled:bg-gray-300 disabled:cursor-not-allowed
                                           focus:outline-none focus:ring-2 focus:ring-primary/50"
                                aria-label="Search"
                            >
                                {searchLoading ? (
                                    <Loader2 size={16} className="animate-spin" />
                                ) : (
                                    <Search size={16} />
                                )}
                            </button>
                        </div>

                        {/* Error message - Mobile */}
                        {searchError && (
                            <div className="mt-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2 flex items-start gap-2">
                                <X size={14} className="text-red-500 mt-0.5 flex-shrink-0" />
                                <span className="text-xs text-red-700">{searchError}</span>
                            </div>
                        )}
                    </div>

                    {/* First Care Logo - Mobile (below search) */}
                    <div className="flex justify-end mt-3">
                        <img
                            src={firstCareLogo}
                            alt="First Care Logo"
                            className="h-10 w-auto object-contain"
                        />
                    </div>
                </div>
            </div>
        </header>
    );
}